/* cf-start-again-fix.js - Click Fencing, 2026-09-02
 *
 * Pete: "i pressed start again and it redrew it. however i thought start again should clear
 *        the entries and start back at the add sections page"
 *
 * ---------------------------------------------------------------------------------------
 * WHY IT DID NOT CLEAR - two facts, both read out of cf-calc-memory.js and reproduced live.
 *
 * 1. THE BUTTON SITS INSIDE THE FORM IT IS TRYING TO CLEAR. cf-calc-memory binds, in capture
 *    phase, on .calculator-page:
 *
 *      r.addEventListener("click", function(e){
 *        var t=e.target;
 *        t && t.closest && t.closest('input,select,button,[role="switch"],label') && save()
 *      }, true)
 *
 *    "Start again" is a <button> inside .calculator-page, so pressing it counts as a form
 *    interaction and schedules a save of the very fence being discarded.
 *
 * 2. THAT SAVE IS DEBOUNCED, SO IT LANDS AFTER THE WIPE:
 *
 *      function save(){ restoring || (clearTimeout(saveTimer), saveTimer = setTimeout(
 *        function(){ ... sessionStorage.setItem(KEY, JSON.stringify(snapshot())) ... },
 *        SAVE_DEBOUNCE)) }
 *
 *    Their startAgain() removes the key and sets location.href - but a page does not stop
 *    dead on navigation, and the confirm() dialog holds it open longer still. The debounced
 *    write fires in that window and puts the fence straight back.
 *
 * REPRODUCED on PerfMesh 8725mm raked with a gate: pressed Start again, the page reloaded to
 * the bare path, and the fence was still 8725 raked with the gate. The rewritten key was 20
 * seconds old - newer than the click that was supposed to delete it - and a search of every
 * cookie, localStorage and sessionStorage entry found "8725" in that one key and nowhere
 * else. So the config was not coming from some other store; it was the wipe losing a race
 * with its own save.
 *
 * ---------------------------------------------------------------------------------------
 * THE FIX. Take the click in CAPTURE phase on document - which runs before their capture
 * listener on .calculator-page, since document is the outer node - and stop the event dead.
 * Their save is therefore never scheduled and their startAgain never runs. We then wipe and
 * navigate ourselves, with a marker on the URL, and wipe once more on the way back in before
 * their restore can fire. Belt and braces, because the losing side of that race is a customer
 * who asked for a blank sheet and got their old fence back.
 *
 * WHAT IT CLEARS, AND WHAT IT DELIBERATELY DOES NOT. The button says it "clears the fence you
 * have configured on this page", so it clears the WORKING state only:
 *
 *    cleared   cf-calc:<path>      the replay snapshot
 *              *_session           the calculator's own working session (holds currentTab,
 *                                  which is also why Pete landed back on review rather than
 *                                  at Add Sections)
 *              *_quote             the working quote breakdown
 *              *_active_quote      the "you are editing saved quote N" pointer - dropping it
 *                                  is the difference between a blank sheet and silently
 *                                  resuming an edit
 *
 *    KEPT      *_quotes            the customer's SAVED PLANS. Start again must never destroy
 *              *_quote_counter     work the customer deliberately saved, and no path through
 *                                  this file touches those keys.
 */
(function () {
  'use strict';

  if (window.__cfStartAgainFix) return;
  window.__cfStartAgainFix = true;

  var CALC_PATHS = [
    '/pages/perfmesh-fence', '/pages/blade-fence-calculator',
    '/pages/flat-top-fence-calculator', '/pages/base-mount-batten-calculator',
    '/pages/glass-fencing', '/pages/fence-calculator'
  ];
  var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  if (CALC_PATHS.indexOf(path) === -1) return;

  var MARK = 'cf-reset';

  /* Saved plans are the customer's own work. Nothing below may remove them. */
  function isSavedPlan(k) {
    return /_quotes$/.test(k) || /_quote_counter$/.test(k);
  }
  function isWorkingState(k) {
    if (isSavedPlan(k)) return false;
    return /_session$/.test(k) || /_quote$/.test(k) || /_active_quote$/.test(k);
  }

  function isThisPage(k, prefix) {
    if (k.indexOf(prefix) !== 0) return false;
    return (k.slice(prefix.length).replace(/\/+$/, '') || '/') === path;
  }

  function wipe() {
    try {
      sessionStorage.removeItem('cf-calc:' + path);
      sessionStorage.removeItem('cf-calc:' + location.pathname);
    } catch (e) {}

    [localStorage, sessionStorage].forEach(function (store) {
      try {
        var kill = [];
        for (var i = 0; i < store.length; i++) {
          var k = store.key(i);
          if (!k) continue;
          /* 2026-09-26: cf-fresh-hold:<path> is the copy cf-fresh-start parks on every visit. Left
             behind, the 26 Sep restore-on-refresh / restore-in-a-job put the old fence straight
             back after Start again. */
          /* 26 Sep review: only THIS page's memory and parked copy — another calculator's fence
             (and its "Reload last fence" offer) is not this page's to clear. */
          if (isThisPage(k, 'cf-calc:') || isThisPage(k, 'cf-fresh-hold:') || isWorkingState(k)) kill.push(k);
        }
        kill.forEach(function (k) { try { store.removeItem(k); } catch (e) {} });
      } catch (e) {}
    });
  }

  function inJob() {
    try {
      var j = JSON.parse(localStorage.getItem('cf_job') || 'null');
      if (sessionStorage.getItem('cf_job_active') !== '1' || !j) return false;
      /* 26 Sep: only when THIS fence type is in the job — same rule as cf-job-ui's jobActive() */
      var CALC_OF = { '/pages/glass-fencing': 'Frameless', '/pages/perfmesh-fence': 'PerfMesh',
                      '/pages/flat-top-fence-calculator': 'FlatTop', '/pages/blade-fence-calculator': 'Blade' };
      var ct = CALC_OF[location.pathname.replace(/\/+$/, '')];
      var items = (j.plan && j.plan.items) || [], runs = j.runs || [];
      if (ct) return items.length ? items.some(function (it) { return it.calcType === ct; }) : runs.some(function (r) { return r.calcType === ct; });
      return !!(items.length || runs.length || j.mixIntent);
    } catch (e) { return false; }
  }
  function wipeJob(allCalcs) {
    /* 26 Sep review: a page that is not part of the job leaves the job alone — it may be the
       one the customer is building in another tab, and it has no effect on this page. */
    if (!allCalcs) return;
    try { localStorage.removeItem('cf_job'); } catch (e) {}
    ['cf_job_active', 'cf_job_applied', 'cf_plan_prefill', 'cf-job-arrive', 'cf_job_goto', 'cf_job_hop'].forEach(function (k) {
      try { sessionStorage.removeItem(k); } catch (e) {}
    });
    /* every calculator's working copy, not just this page's — the job spans all four */
    if (allCalcs) try {
      var kill = [];
      for (var i = 0; i < sessionStorage.length; i++) {
        var k = sessionStorage.key(i);
        if (k && (k.indexOf('cf-calc:') === 0 || k.indexOf('cf-fresh-hold:') === 0)) kill.push(k);
      }
      kill.forEach(function (k) { try { sessionStorage.removeItem(k); } catch (e) {} });
    } catch (e) {}
  }

  /* CAPTURE on document, so we are ahead of cf-calc-memory's capture listener on
     .calculator-page. stopImmediatePropagation means their save is never scheduled and their
     own startAgain never runs - we own the button from here. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    var b = (t && t.closest) ? t.closest('#cf-start-again') : null;
    if (!b) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    var ok = true;
    try {
      ok = window.confirm(inJob()
        ? 'Start again? This clears your whole fence job — every section and fence type. Any plans you have saved are kept.'
        : 'Start again? This clears the fence you have configured on this page. Any plans you have saved are kept.');
    } catch (err) { ok = true; }
    if (!ok) return;

    wipe();
    /* 2026-09-26: in a mixed-fence job the job's plan refilled the sections after a reset. Start
       again in a job starts the whole job again (saved plans are still kept). */
    wipeJob(inJob());   // 26 Sep: in a job, the whole job and every calculator's fence; otherwise just this page
    try {
      location.replace(location.pathname + '?' + MARK + '=1');
    } catch (err) {
      location.href = location.pathname + '?' + MARK + '=1';
    }
  }, true);

  /* Coming back in after a reset: wipe again before cf-calc-memory boots (it waits on a 400ms
     interval, we run at defer time), then take the marker off the URL so a refresh or a shared
     link is clean. replaceState, so it leaves no extra history entry to go "back" into. */
  if (new RegExp('[?&]' + MARK + '=1').test(location.search)) {
    wipe();
    try {
      history.replaceState(null, '', location.pathname);
    } catch (e) {}
  }

  try { console.log('[cf-start-again-fix] installed'); } catch (e) {}
})();
